import { serverTimestamp, type FieldValue } from 'firebase/firestore'
import {
  typedCollection,
  typedDoc,
  getCollection,
  updateDocument,
  deleteDocument,
  addDoc,
  orderBy,
  where,
} from '@/lib/firebase/firestore'
import type { ClientEngagementDocument, EngagementStatus, UserDocument } from '@/types'
import type { EngagementFormValues } from '../types'
import { createClientUserByAdminFn } from '@/lib/firebase/functions'

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

function deriveEstimateAmount(form: Pick<EngagementFormValues, 'lineItems' | 'discountPercent'>): number {
  const subtotal = (form.lineItems ?? []).reduce(
    (s, i) => s + (Number.isFinite(i.lineTotal) ? i.lineTotal : 0),
    0
  )
  const pct = form.discountPercent ?? 0
  return round2(subtotal * (1 - pct / 100))
}

const engagementsCol = () =>
  typedCollection<ClientEngagementDocument>('clientEngagements')

const usersCol = () => typedCollection<UserDocument>('users')

type EngagementCreatePayload = Omit<ClientEngagementDocument, 'id' | 'createdAt' | 'updatedAt'> & {
  createdAt: FieldValue
  updatedAt: FieldValue
}

export const adminClientEngagementsService = {
  async listEngagements(
    statusFilter?: EngagementStatus
  ): Promise<Array<ClientEngagementDocument & { id: string }>> {
    if (statusFilter) {
      return getCollection(
        engagementsCol(),
        where('status', '==', statusFilter),
        orderBy('createdAt', 'desc')
      )
    }
    return getCollection(engagementsCol(), orderBy('createdAt', 'desc'))
  },

  async createEngagement(data: EngagementFormValues): Promise<string> {
    const derivedEstimate = deriveEstimateAmount(data)
    const payload: EngagementCreatePayload = {
      clientUserId: data.clientUserId,
      clientEmail: data.clientEmail,
      clientName: data.clientName,
      title: data.title,
      ...(data.description ? { description: data.description } : {}),
      ...(data.assignedMemberIds.length > 0 ? { assignedMemberIds: data.assignedMemberIds } : {}),
      ...(data.equipmentIds.length > 0 ? { equipmentIds: data.equipmentIds } : {}),
      ...(data.scheduledDate ? { scheduledDate: data.scheduledDate } : {}),
      ...(derivedEstimate > 0 ? { estimateAmount: derivedEstimate } : {}),
      ...(data.estimateNote ? { estimateNote: data.estimateNote } : {}),
      ...(data.driveLink ? { driveLink: data.driveLink } : {}),
      status: data.status,
      ...(data.sourceRequestId ? { sourceRequestId: data.sourceRequestId } : {}),
      ...(data.lineItems.length > 0 ? { lineItems: data.lineItems } : {}),
      ...(data.discountPercent > 0 ? { discountPercent: data.discountPercent } : {}),
      isArchived: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(
      engagementsCol(),
      payload as unknown as ClientEngagementDocument
    )
    return ref.id
  },

  async updateEngagement(
    id: string,
    data: Partial<EngagementFormValues & {
      equipmentNames?: string[]
      assignedMemberNames?: string[]
    }>
  ): Promise<void> {
    const ref = typedDoc<ClientEngagementDocument>('clientEngagements', id)
    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }
    const fields: Array<keyof EngagementFormValues> = [
      'clientUserId', 'clientEmail', 'clientName', 'title', 'description',
      'scheduledDate', 'estimateNote', 'driveLink',
      'status', 'sourceRequestId',
    ]
    for (const key of fields) {
      if (data[key] !== undefined) updates[key] = data[key]
    }
    if (data.assignedMemberIds !== undefined) updates.assignedMemberIds = data.assignedMemberIds
    if (data.assignedMemberNames !== undefined) updates.assignedMemberNames = data.assignedMemberNames
    if (data.equipmentIds !== undefined) updates.equipmentIds = data.equipmentIds
    if (data.equipmentNames !== undefined) updates.equipmentNames = data.equipmentNames
    if (data.lineItems !== undefined) updates.lineItems = data.lineItems
    if (data.discountPercent !== undefined) updates.discountPercent = data.discountPercent
    // Derive estimateAmount whenever lineItems or discountPercent change
    if (data.lineItems !== undefined || data.discountPercent !== undefined) {
      const lineItems = data.lineItems ?? []
      const discountPercent = data.discountPercent ?? 0
      updates.estimateAmount = deriveEstimateAmount({ lineItems, discountPercent })
    }
    await updateDocument(ref, updates as unknown as ClientEngagementDocument)
  },

  async deleteEngagement(id: string): Promise<void> {
    const ref = typedDoc<ClientEngagementDocument>('clientEngagements', id)
    await deleteDocument(ref)
  },

  async lookupUserByEmail(
    email: string
  ): Promise<(UserDocument & { id: string }) | null> {
    const results = await getCollection(
      usersCol(),
      where('email', '==', email.toLowerCase().trim())
    )
    return results[0] ?? null
  },

  async createClientUser(
    email: string,
    displayName: string
  ): Promise<(UserDocument & { id: string })> {
    const { data } = await createClientUserByAdminFn({ email, displayName })
    const looked = await this.lookupUserByEmail(email)
    if (looked) return looked
    // Fallback synthesis if the users/{uid} doc isn't visible yet via query.
    return {
      id: data.uid,
      uid: data.uid,
      email: email.toLowerCase().trim(),
      displayName,
      photoURL: null,
      role: 'user',
      createdAt: new Date(),
      updatedAt: new Date(),
    }
  },
}
